// Browsers often report a blank `File.type` for Office binary formats (notably
// .xlsb). Falling back to a fixed default would tag the S3 object — and the
// presign request — with the wrong content type, which the downstream
// classification pipeline keys off of. Derive the type from the extension when
// the browser doesn't give us one.
const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  xlsb: "application/vnd.ms-excel.sheet.binary.macroEnabled.12",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
  csv: "text/csv",
};

const FALLBACK_CONTENT_TYPE = "application/octet-stream";

// MIME types the browser reports when it can't identify the file. These are as
// useless to the classification pipeline as a blank type, so we treat them the
// same and prefer the extension-derived type when one is available.
const GENERIC_CONTENT_TYPES = new Set([FALLBACK_CONTENT_TYPE, "application/binary"]);

/** The file's own MIME when the browser provides a specific one, otherwise a
 *  type derived from its extension (octet-stream for unknown extensions).
 *  Generic browser MIMEs (octet-stream/binary) are treated as absent, since
 *  browsers commonly report them for Office binary formats like .xlsb. */
export function resolveUploadContentType(file: File): string {
  if (file.type && !GENERIC_CONTENT_TYPES.has(file.type)) {
    return file.type;
  }
  const extension = file.name.split(".").pop()?.toLowerCase();
  return (
    (extension && EXTENSION_CONTENT_TYPES[extension]) || file.type || FALLBACK_CONTENT_TYPE
  );
}
